import { db } from '../config/firebase';
import {
    collection,
    addDoc,
    query,
    orderBy,
    limit,
    getDocs,
    where,
} from 'firebase/firestore';

const LOGS_COLLECTION = 'SummerSchool-2026-logs';

export const LogLevel = {
    INFO: 'info',
    WARNING: 'warning',
    ERROR: 'error',
};

/**
 * Write a log entry to Firestore
 * @param {string} level - Log level (info, warning, error)
 * @param {string} message - Log message
 * @param {Object} metadata - Additional data to log
 * @param {string} [documentId] - Optional Firestore document ID for audit purposes
 * @returns {Promise<string>} - Document ID of the created log
 */
export async function log(level, message, metadata = {}, documentId = null) {
    const logEntry = {
        timestamp: new Date().toISOString(),
        level,
        message,
        metadata,
        documentId, // Store at top level for easier querying
    };

    try {
        const docRef = await addDoc(collection(db, LOGS_COLLECTION), logEntry);
        return docRef.id;
    } catch (error) {
        console.error('Failed to write log:', error);
        // Fall back to console logging if Firestore write fails
        console.log(JSON.stringify(logEntry));
    }
}

/**
 * Convenience methods for different log levels
 */
export const logger = {
    info: (message, metadata, documentId) =>
        log(LogLevel.INFO, message, metadata, documentId),
    warning: (message, metadata, documentId) =>
        log(LogLevel.WARNING, message, metadata, documentId),
    error: (message, metadata, documentId) =>
        log(LogLevel.ERROR, message, metadata, documentId),
};

/**
 * Retrieve recent logs
 * @param {number} limit - Number of logs to retrieve
 * @returns {Promise<Array>} - Array of log entries
 */
export async function getRecentLogs(limitCount = 100) {
    const logsRef = collection(db, LOGS_COLLECTION);
    const q = query(logsRef, orderBy('timestamp', 'desc'), limit(limitCount));

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
    }));
}

/**
 * Get all enrolment logs
 * @returns {Promise<Array>} - Array of enrolment log entries
 */
export async function getEnrolmentLogs() {
    const logsRef = collection(db, LOGS_COLLECTION);
    const q = query(
        logsRef,
        where('message', '==', 'Enrolment saved'),
        orderBy('timestamp', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
    }));
}
